import React, { FC, useEffect, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { FaqsGeneratorResponse } from "../../../services/getAiResponse";
import { getPageTitle } from "../util";

interface FAQ {
  question: string;
  answer: string;
}

interface FaqsTabGeneratorProps {
  content: string;
  focusKeyword: string;
  title?: string;
  metaDescription?: string;
}

const hasH1Title = (content: string): boolean => {
  if (!content) return false;
  const h1Regex = /^(#\s.+)|<h1[^>]*>.*<\/h1>/im;
  return h1Regex.test(content);
};

export const FaqsTabGenerator: FC<FaqsTabGeneratorProps> = ({
  focusKeyword,
  title = "",
  metaDescription = "",
  content,
}) => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const extractedTitle =
    title || getPageTitle(content) || content.substring(0, 60);

  const generateFAQs = async () => {
    let newError = "";
    if (!hasH1Title(content)) {
      newError = "H1 title is not present in the content.";
    } else if (!extractedTitle || extractedTitle.trim().length < 10) {
      newError = "Title is not available in your content or is too short.";
    } else if (!focusKeyword || focusKeyword.trim().length < 1) {
      newError = "Please enter a focus keyword to generate FAQs.";
    }

    if (newError) {
      setError(newError);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await FaqsGeneratorResponse(
        extractedTitle,
        focusKeyword,
        metaDescription || "",
        "English",
        3,
        "Professional"
      );

      if (response?.trim()) {
        let faqsArray: FAQ[] = JSON.parse(response);

        if (faqsArray.length > 3) {
          faqsArray = faqsArray.slice(0, 3);
        } else if (faqsArray.length < 3) {
          while (faqsArray.length < 3) {
            faqsArray.push({
              question: "Placeholder Question",
              answer: "Placeholder Answer",
            });
          }
        }

        setFaqs(faqsArray);
      }
    } catch (err) {
      console.error("FAQ generation error:", err);
      setError("Failed to generate FAQs. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (focusKeyword.trim() && content.trim() && hasH1Title(content)) {
      setError("");
      generateFAQs();
    } else {
      setError(
        "No FAQs generated yet. Please ensure you've entered both a valid title and a focus keyword."
      );
    }
  }, [focusKeyword, content]);

  return (
    <div style={{ padding: "1rem" }}> {/* Reduced padding from 2rem to 1rem */}
      {isLoading && (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {faqs.length > 0 ? (
        faqs.map((faq, index) => (
          <div key={index} className="mb-3"> {/* Replaced Card with div */}
            <h6 style={{ fontWeight: "bold" }}>{faq.question}</h6>
            <p className="text-muted">{faq.answer}</p>
          </div>
        ))
      ) : (
        !isLoading &&
        !error && (
          <p className="text-muted text-center">
            No FAQs generated yet. Please ensure you've entered both a valid
            title and a focus keyword.
          </p>
        )
      )}
    </div>
  );
};